import { useLang } from "@/i18n/LanguageContext";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { ASSETS } from "@/lib/assets";

export default function CtaBanner({ onUpload }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <section ref={ref} data-testid="cta-banner" className="relative mt-24 overflow-hidden">
      <div className="relative min-h-[520px] overflow-hidden rounded-t-[80px] bg-[color:var(--sentinel)] md:min-h-[560px] md:rounded-t-[120px]">
        <motion.img
          src={ASSETS.ctaBg}
          alt={t.ctaBanner.imageAlt}
          loading="lazy"
          style={{ y: imageY, scale: imageScale }}
          className="absolute inset-0 h-full w-full object-cover opacity-35 mix-blend-luminosity"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--sentinel-darker)] via-[color:var(--sentinel)]/90 to-[color:var(--sentinel)]/75" />

        <div className="relative z-10 flex min-h-[520px] flex-col items-center justify-center px-6 py-20 text-center md:min-h-[560px]">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl text-3xl text-white md:text-[44px] md:leading-[1.08]"
          >
            {t.ctaBanner.title}
          </motion.h2>
          <motion.button
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            type="button"
            onClick={onUpload}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-[color:var(--sentinel)] transition hover:bg-[color:var(--sentinel-tint)]"
            data-testid="cta-banner-button"
          >
            {t.ctaBanner.cta}
            <ArrowUpRight className="h-4 w-4" />
          </motion.button>
        </div>
      </div>
    </section>
  );
}
